import { Observable, Scheduler } from '@reactivex/rxjs';
import { scaleUtc } from 'd3-scale';
import { Gestures } from 'rxjs-gestures';

import hoistStatics from 'react-recompose/hoistStatics';
import mapPropsStream from 'react-recompose/mapPropsStream';
import createEventHandler from 'react-recompose/createEventHandler';

export const withPanZoom = hoistStatics(
    mapPropsStream(propsStream => {
        let { handler: onWheel, stream: wheels } = createEventHandler();
        let { handler: onBrushTimeChange, stream: brushes } = createEventHandler();
        let { handler: onScrollTouchStart, stream: scrollTouchStarts } = createEventHandler();

        wheels = wheels
            .map(pluckWheelEventProps)
            .auditTime(0, Scheduler.animationFrame)
            .publish((wheels) => wheels
                .takeUntil(wheels.debounceTime(500))
                .concat(Observable.of({ isWheelScrolling: false }))
                .repeat()
            )
            .publish()
            .refCount();

        let props = Observable.from(propsStream);
        let scrolls = wheelAndPanScrolls(wheels, scrollTouchStarts);
        let zooms = wheels.filter(wheelShouldZoom).map(decorateWithTargetCoords);

        return Observable.merge(
                zooms.map(zoom => ({ zoom })),
                props.map(props => ({ props })),
                brushes.map(brush => ({ brush })),
                scrolls.map(scroll => ({ scroll })),
            )
            .scan(
                (memo, { zoom, props, brush, scroll }) =>
                       zoom ? zoomTimeRange(memo, zoom)
                    : brush ? brushTimeRange(memo, brush)
                    : props ? updateTimeRange(memo, props)
                    : scroll ? scrollTimeRange(memo, scroll)
                    : memo,
                {}
            )
            .pluck('props')
            .map(props => ({
                ...props,
                onWheel,
                onScrollTouchStart,
                onBrushTimeChange
            }));
    })
);

function wheelAndPanScrolls(wheels, scrollTouchStarts) {
    const mapDrags = (flip = 1, type) => drags =>
        drags
            .stopPropagation() // <-- kill bubbling
            .decelerate(25) // <-- sprinkle in some physics to make things fun
            // match the WheelEvent API
            .map(({ buttons, movementX, movementY }) => ({
                type,
                buttons,
                deltaX: movementX * flip,
                deltaY: movementY * flip
            }))
            .concat(Observable.of({ buttons: 0 }))
            .startWith({ buttons: 1 });

    const wheelScrolls = wheels.filter(wheelShouldScroll);
    const panScrolls = Gestures
        .pan(scrollTouchStarts.filter((e) => !e.shiftKey && !e.button))
        .repeat().flatMap(mapDrags(-1, 'wheel'));

    return wheelScrolls.merge(panScrolls);
}

export function getScales(memo, props = memo.props) {
    const timeScale = memo.timeScale || props.timeScale;
    const zoomScale = memo.zoomScale || props.zoomScale;
    const timeDomain = memo.timeDomain || props.timeDomain;
    const zoomDomain = memo.zoomDomain || props.zoomDomain;
    return { timeScale, timeDomain, zoomScale, zoomDomain };
}

function pluckWheelEventProps(wheel) {
    return {
        type: 'wheel',
        pageX: wheel.pageX,
        pageY: wheel.pageY,
        deltaX: wheel.deltaX,
        deltaY: wheel.deltaY,
        target: wheel.target,
        isWheelScrolling: true,
        currentTarget: wheel.currentTarget,
    };
}

function wheelShouldZoom(wheelProps) {
    const deltaX = wheelProps.deltaX || 0;
    // If no deltaX, zoom if deltaY exists
    if (!deltaX) {
        return !!wheelProps.deltaY;
    }
    const closenessThreshold = 20;
    const deltaY = wheelProps.deltaY;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY || 0);
    // If deltaX and deltaY are really close, then choose scroll over zoom
    if (Math.abs(absDeltaX - absDeltaY) < closenessThreshold) {
        return false;
    }
    // Otherwise, zoom if deltaY is larger than deltaX
    return absDeltaY > absDeltaX;
}

function wheelShouldScroll(wheelProps) {
    return !wheelShouldZoom(wheelProps);
}

function decorateWithTargetCoords(wheelProps) {
    const documentElement = document.documentElement;
    const { pageY, pageX, currentTarget, target } = wheelProps;
    const { top, left } = (currentTarget || target).getBoundingClientRect();
    return {
        ...wheelProps,
        targetY: Math.round(pageY - (top + window.pageYOffset - documentElement.clientTop)),
        targetX: Math.round(pageX - (left + window.pageXOffset - documentElement.clientLeft))
    };
}

function updateTimeRange(memo, props) {
    const scales = getScales(memo, props);
    const isPanZooming = memo.isPanZooming || props.isPanZooming;
    const isPanScrolling = memo.isPanScrolling || props.isPanScrolling;
    return {
        ...memo, ...scales,
        isPanZooming, isPanScrolling,
        props: {
            ...props, ...scales,
            isPanZooming, isPanScrolling
        }
    };
}

function brushTimeRange(memo, brush) {
    let { props } = memo;
    let brushType = brush.type;
    let zoomDomain = brush.timeDomain;
    if (!zoomDomain) { return memo; }
    let zoomScale = scaleUtc().domain(zoomDomain);
    let isPanScrolling = brushType !== 'done' && (brushType === 'pan');
    let isPanZooming = brushType !== 'done' && (brushType === 'left' || brushType === 'right');
    return {
        ...memo,
        zoomScale, zoomDomain,
        isPanScrolling, isPanZooming,
        props: {
            ...props,
            zoomScale, zoomDomain,
            isPanScrolling, isPanZooming,
        }
    };
}

const nanosecondsPerMillisecond = 1000000;

function scrollTimeRange(memo, scroll) {
    let { props } = memo;
    let { minTime, maxTime } = props;
    let { timeScale, zoomScale, zoomDomain } = getScales(memo, props);
    if (!timeScale || !zoomScale) { return memo; }
    let [beginTime, endTime] = zoomDomain;
    let timeDuration = +endTime - +beginTime;
    let duration_ns = timeDuration * nanosecondsPerMillisecond;
    let deltaT = (scroll.deltaX * 1.25) * (duration_ns / zoomScale.range()[1]) / nanosecondsPerMillisecond || 0;

    if (+beginTime + deltaT < minTime) {
        if (beginTime > minTime) {
            zoomDomain = [minTime, minTime + timeDuration];
            zoomScale = scaleUtc().domain(zoomDomain);
        }
    } else if (+endTime + deltaT > maxTime) {
        if (endTime < maxTime) {
            zoomDomain = [maxTime - timeDuration, maxTime];
            zoomScale = scaleUtc().domain(zoomDomain);
        }
    } else if (deltaT) {
        zoomDomain = zoomDomain.map(t => +t + deltaT);
        zoomScale = scaleUtc().domain(zoomDomain);
    }

    return {
        ...memo,
        zoomScale, zoomDomain,
        isPanScrolling: !!scroll.buttons,
        props: {
            ...props,
            zoomScale, zoomDomain,
            isPanScrolling: !!scroll.buttons,
            isWheelScrolling: !!scroll.isWheelScrolling
        }
    };
}

function zoomTimeRange(memo, zoom) {
    let { props } = memo;
    let { timeScale, zoomScale, zoomDomain } = getScales(memo, props);
    if (!timeScale || !zoomScale) { return memo; }
    let timeDuration = 0;
    let pointerDelta = zoom.deltaY || 0;
    let [beginTime, endTime] = zoomDomain.map((x) => +x);
    let newEndTime = endTime;
    let newBeginTime = beginTime;
    let { minTime, maxTime } = props;
    let pointerPosition = zoom.targetX || 0;
    let pointerOffset = memo.props.yAxisWidth || 0;
    let clampedPosition = Math.max(
        0,
        Math.min(pointerPosition - pointerOffset, zoomScale.range()[1])
    );
    let zoomDelta = Math.max(Math.abs(pointerDelta || 0), 1) + 1;
    let zoomFactor = 1 + (Math.sign(pointerDelta || 0) * (Math.log(zoomDelta) / 60));

    let middle = zoomScale.invert(clampedPosition).getTime();

    // Duration constraint
    timeDuration = (endTime - beginTime) * zoomFactor;
    newEndTime = middle + (endTime - middle) * zoomFactor;
    newBeginTime = middle - (middle - beginTime) * zoomFactor;
    if (timeDuration < 1000) {
        timeDuration = 1000;
    } else if (timeDuration > maxTime - minTime) {
        timeDuration = maxTime - minTime;
    }
    // Range constraint
    if (newBeginTime < minTime) {
        newBeginTime = minTime;
        newEndTime = newBeginTime + timeDuration;
    }
    if (newEndTime > maxTime) {
        newEndTime = maxTime;
        newBeginTime = newEndTime - timeDuration;
    }
    // If new bounds don't match the current bounds, regenerate the time scale
    if (newBeginTime !== beginTime || newEndTime !== endTime) {
        zoomDomain = [Math.max(minTime, newBeginTime), Math.min(maxTime, newEndTime)];
        zoomScale = scaleUtc().domain(zoomDomain);
    }

    return {
        ...memo,
        zoomDomain,
        zoomScale,
        props: {
            ...props,
            zoomScale,
            zoomDomain,
            isPanZooming: zoom.isPanZooming,
            isWheelScrolling: !!zoom.isWheelScrolling
        }
    };
}
